import type { Organization, User, UserWritePayload } from '@app/shared/types';
import { MappedTemplate, Project, Role, Template, UserForm } from './types';

export const resolveUserOrganization = ({
  orgId,
  organizations,
  userOrganizations,
}: {
  orgId: string;
  organizations?: Array<Pick<Organization, 'id' | 'name'>>;
  userOrganizations?: Array<Pick<Organization, 'id' | 'name'>>;
}): Organization => {
  const fromList = organizations?.find(org => org.id === orgId);
  if (fromList) return fromList;

  const fromUser = userOrganizations?.find(org => org.id === orgId);
  if (fromUser) return fromUser;

  return {
    id: orgId,
    name: 'Unknown Team',
    usersCount: 0,
  };
};

export const getUserOrganizationIds = (
  user?:
    | Pick<User, 'organizations'>
    | Pick<UserWritePayload, 'organizationIds'>
    | null,
): string[] => {
  if (!user) return [];

  if ('organizationIds' in user && Array.isArray(user.organizationIds)) {
    return user.organizationIds.filter(Boolean);
  }

  return ((user as Pick<User, 'organizations'>).organizations ?? [])
    .map(org => org.id)
    .filter(Boolean);
};

export const getUserOrganizationNames = (
  user?: Pick<User, 'organizations'> | null,
): string[] => (user?.organizations ?? []).map(org => org.name).filter(Boolean);

/** Maps form state to create/update API payload. */
export const toUserWritePayload = (
  data: UserForm,
  id?: string,
): UserWritePayload => {
  const { organizationIds = [], attributes = [], ...rest } = data;

  return {
    ...rest,
    ...(id ? { id } : {}),
    attributes,
    organizationIds: organizationIds.filter(Boolean),
  };
};

export const mapTemplate = (template?: Template): MappedTemplate[] => {
  if (!template || !template.projects || template.projects.length === 0) {
    return [];
  }

  return template.projects.map((project: Project) => {
    if (!project.roles || project.roles.length === 0) {
      return {
        id: project?.name,
        label: project?.name,
        children: [],
      };
    }

    const roles = project.roles.map((role: Role) => {
      const attributes = role?.attributes || [];
      const isSelfReferential =
        attributes.length === 1 && attributes[0] === role.name;

      return {
        id: role?.name,
        label: role?.name,
        children: isSelfReferential
          ? []
          : attributes.map(attribute => ({
              id:
                attribute === role.name
                  ? `${role.name}:${attribute}`
                  : attribute,
              label: attribute,
            })),
      };
    });

    return {
      id: project?.name,
      label: project?.name,
      children: roles,
    };
  });
};

export const getSelectedPermissionsWithParents = (
  selected: Set<string>,
  templates?: MappedTemplate[],
): Set<string> => {
  const result = new Set<string>(selected);

  for (const template of templates || []) {
    if (!template.children || template.children.length === 0) {
      continue;
    }

    let allRolesSelected = true;

    for (const role of template.children) {
      if (!role.children || role.children.length === 0) {
        if (!selected.has(role.id)) {
          allRolesSelected = false;
        } else {
          result.add(role.id);
        }
        continue;
      }

      const allAttributesSelected = role.children.every(attr =>
        selected.has(attr.id),
      );

      if (allAttributesSelected) {
        result.add(role.id);
      } else {
        allRolesSelected = false;
      }
    }

    if (allRolesSelected) {
      result.add(template.id);
    }
  }

  return result;
};

export const getLeafNodesOnly = (
  selected: string[],
  templates: MappedTemplate[],
): string[] => {
  const leafNodes: string[] = [];
  const parentIds = new Set<string>();

  for (const template of templates) {
    parentIds.add(template.id);

    if (template.children) {
      for (const role of template.children) {
        if (role.children && role.children.length > 0) {
          parentIds.add(role.id);
        }
      }
    }
  }

  for (const attr of selected) {
    if (!parentIds.has(attr)) {
      const parts = attr.split(':');
      if (parts.length === 2 && parts[0] === parts[1]) {
        leafNodes.push(parts[0]);
      } else {
        leafNodes.push(attr);
      }
    }
  }

  return leafNodes;
};
